import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLanguage } from '@/context/LanguageContext';

const SITE_URL = 'https://blacklineps.com';
const SITE_NAME = 'Blackline Premium Solutions';
const DEFAULT_IMAGE = 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?w=1200&q=80';

const SEOHead = ({
  title,
  description,
  path = '',
  image = DEFAULT_IMAGE,
  type = 'website',
  schema,
  noindex = false
}) => {
  const { lang } = useLanguage();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : `${SITE_NAME} | Windows & Doors`;
  const canonicalEN = `${SITE_URL}${path}`;
  const canonicalES = `${SITE_URL}/es${path === '/' ? '' : path}`;
  const canonical = lang === 'es' ? canonicalES : canonicalEN;

  const defaultSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: SITE_URL,
    logo: `${SITE_URL}/blackline-logo-cropped.png`,
    description: 'Premium window and door systems for modern architecture in Houston and Dallas, Texas.',
    address: [
      {
        '@type': 'PostalAddress',
        addressLocality: 'Houston',
        addressRegion: 'TX',
        addressCountry: 'US'
      },
      {
        '@type': 'PostalAddress',
        addressLocality: 'Dallas',
        addressRegion: 'TX',
        addressCountry: 'US'
      }
    ],
    telephone: '+1-346-615-6860',
    email: 'info@blacklineps.com',
    sameAs: []
  };

  return (
    <Helmet>
      <html lang={lang} />
      <title>{fullTitle}</title>
      <meta name="description" content={description || 'Premium window and door systems designed for modern architecture. Serving Houston and Dallas, Texas.'} />
      {noindex && <meta name="robots" content="noindex,nofollow" />}

      {/* Canonical + hreflang */}
      <link rel="canonical" href={canonical} />
      <link rel="alternate" hrefLang="en" href={canonicalEN} />
      <link rel="alternate" hrefLang="es" href={canonicalES} />
      <link rel="alternate" hrefLang="x-default" href={canonicalEN} />

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description || 'Premium window and door systems for modern architecture.'} />
      <meta property="og:image" content={image} />
      <meta property="og:url" content={canonical} />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === 'es' ? 'es_MX' : 'en_US'} />
      <meta property="og:locale:alternate" content={lang === 'es' ? 'en_US' : 'es_MX'} />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description || 'Premium window and door systems.'} />
      <meta name="twitter:image" content={image} />

      {/* JSON-LD Schema */}
      <script type="application/ld+json">
        {JSON.stringify(schema || defaultSchema)}
      </script>
    </Helmet>
  );
};

// Schema generators
export const generateLocalBusinessSchema = (city, phone) => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  name: `Blackline Premium Solutions - ${city}`,
  description: `Premium windows and doors in ${city}, Texas`,
  url: SITE_URL,
  telephone: phone,
  email: 'info@blacklineps.com',
  address: {
    '@type': 'PostalAddress',
    addressLocality: city,
    addressRegion: 'TX',
    addressCountry: 'US'
  },
  openingHours: 'Mo-Fr 08:30-17:30',
  priceRange: '$$$'
});

export const generateProductSchema = (product, lang = 'en') => {
  const loc = product[lang] || product.en || {};
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: loc.name,
    description: loc.description || loc.one_liner,
    image: product.image_url,
    brand: { '@type': 'Brand', name: 'Blackline Premium Solutions' },
    manufacturer: { '@type': 'Organization', name: 'Blackline Premium Solutions' },
    url: `${SITE_URL}/products/${product.slug}`
  };
};

export const generateBreadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((item, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: item.name,
    item: `${SITE_URL}${item.path}`
  }))
});

export default SEOHead;
